/**
 * DocumentProcessingEnrichmentWidget — AC-11/12 combined widget.
 *
 * AC-11: Replaces the two old widgets (DocumentProcessingModelWidget + ContextualEnrichmentWidget)
 *   with ONE widget: provider dropdown + model dropdown (same filtered list as P1 AIProviderForm)
 *   + contextual enrichment toggle. One selected model serves both document processing and
 *   contextual enrichment.
 *
 * AC-12: Auto-saves: every change persists immediately without a save button.
 *   Changes survive reload. Backend rejects models outside the filtered list (400).
 *   Errors revert the control to the last persisted value.
 *
 * API:
 *   GET  /api/orgs/:id/knowledge/settings/generation-provider   → {provider, savedProvider}
 *   PUT  /api/orgs/:id/knowledge/settings/generation-provider   {provider} → 200 | 400
 *   GET  /api/orgs/:id/knowledge/settings/generation-providers  → [{provider}]
 *   GET  /api/orgs/:id/knowledge/settings/generation-model      → {model, provider}
 *   PUT  /api/orgs/:id/knowledge/settings/generation-model      {model} → 200 | 400
 *   GET  /api/orgs/:id/knowledge/settings/contextual-enrichment → {enabled}
 *   PUT  /api/orgs/:id/knowledge/settings/contextual-enrichment {enabled} → 200 | 403
 *   POST /api/ai-config/models/:provider                        → [{id, name}] (filtered list)
 *
 * LAYERING NOTE (AILLM-7): Lives in components/roros/settings/ but imported by
 * routes/organization/AISettings.tsx. Same intentional coupling as other widgets here.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, Select,
} from '@/components/ui';
import { Switch } from '@/components/ui/Switch/Switch';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { Cpu } from 'lucide-react';
import { PROVIDER_LABELS } from '@/components/ai/aiProviderConstants';

export function DocumentProcessingEnrichmentWidget() {
  const { activeOrgId } = useOrgStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.MANAGE_KNOWLEDGE);
  const queryClient = useQueryClient();

  // Transient selection state — synced from saved on load; reverts on backend 400
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');

  // ── Queries ──────────────────────────────────────────────────────────────────

  // Saved provider
  const { data: providerData } = useQuery({
    queryKey: ['rag-gen-provider', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/generation-provider`,
      );
      if (!res.ok) throw new Error('Failed to fetch generation provider');
      return (await res.json()).data as { provider: string; savedProvider: string | null };
    },
    enabled: !!activeOrgId,
  });

  // Available providers (org's configured, chat-capable)
  const { data: availableProviders } = useQuery({
    queryKey: ['rag-gen-providers', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/generation-providers`,
      );
      if (!res.ok) return [] as Array<{ provider: string }>;
      return (await res.json()).data as Array<{ provider: string }>;
    },
    enabled: !!activeOrgId,
  });

  // Saved model
  const { data: modelData } = useQuery({
    queryKey: ['rag-gen-model', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/generation-model`,
      );
      if (!res.ok) throw new Error('Failed to fetch generation model');
      return (await res.json()).data as { model: string; provider: string };
    },
    enabled: !!activeOrgId,
  });

  // Filtered model list for selectedProvider (same filter as P1 AIProviderForm)
  // POST /api/ai-config/models/:provider — reuse P1 endpoint directly
  const { data: modelsData, isLoading: isModelsLoading } = useQuery({
    queryKey: ['ai-config-models', activeOrgId, selectedProvider],
    queryFn: async () => {
      if (!selectedProvider) return [] as Array<{ id: string; name: string }>;
      const res = await apiFetch(`/api/ai-config/models/${encodeURIComponent(selectedProvider)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) return [] as Array<{ id: string; name: string }>;
      return (await res.json()).data as Array<{ id: string; name: string }>;
    },
    enabled: !!activeOrgId && !!selectedProvider,
  });

  // Contextual enrichment toggle
  const { data: enrichmentData } = useQuery({
    queryKey: ['rag-contextual-enrichment', activeOrgId],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/contextual-enrichment`,
      );
      if (!res.ok) throw new Error('Failed to fetch enrichment setting');
      return (await res.json()).data as { enabled: boolean };
    },
    enabled: !!activeOrgId,
  });

  // ── Sync saved → local selection on initial load ──────────────────────────
  useEffect(() => {
    if (providerData?.provider && !selectedProvider) {
      setSelectedProvider(providerData.provider);
    }
  }, [providerData?.provider]);

  useEffect(() => {
    if (modelData?.model && !selectedModel) {
      setSelectedModel(modelData.model);
    }
  }, [modelData?.model]);

  // When provider changes, reset model (so stale models from old provider don't persist)
  const handleProviderChange = (newProvider: string) => {
    setSelectedProvider(newProvider);
    setSelectedModel('');
    saveProviderMutation.mutate(newProvider);
  };

  // ── Auto-save mutations (AC-12) ───────────────────────────────────────────

  const saveProviderMutation = useMutation({
    mutationFn: async (provider: string) => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/generation-provider`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || t('Failed to update document processing provider'));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rag-gen-provider', activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ['rag-gen-model', activeOrgId] });
    },
    onError: (err: unknown) => {
      // AC-12: revert to saved provider on error
      if (providerData?.provider) setSelectedProvider(providerData.provider);
      showToast.error(
        (err instanceof Error ? err.message : null) || t('Failed to update document processing provider'),
      );
    },
  });

  const saveModelMutation = useMutation({
    mutationFn: async (model: string) => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/generation-model`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || t('Failed to update document processing model'));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rag-gen-model', activeOrgId] });
    },
    onError: (err: unknown) => {
      // AC-12: revert to saved model on backend 400
      if (modelData?.model) setSelectedModel(modelData.model);
      showToast.error(
        (err instanceof Error ? err.message : null) || t('Failed to update document processing model'),
      );
    },
  });

  const saveEnrichmentMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiFetch(
        `/api/orgs/${encodeURIComponent(activeOrgId!)}/knowledge/settings/contextual-enrichment`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || t('Failed to update contextual enrichment setting'));
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rag-contextual-enrichment', activeOrgId] });
    },
    onError: (err: unknown) => {
      showToast.error(
        (err instanceof Error ? err.message : null) || t('Failed to update contextual enrichment setting'),
      );
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
    if (model) saveModelMutation.mutate(model);
  };

  const handleEnrichmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canManage) return;
    saveEnrichmentMutation.mutate(e.target.checked);
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  const enrichmentEnabled = enrichmentData?.enabled ?? false;

  // FW-11b: use shared PROVIDER_LABELS constant (no inline ternary duplication)
  const providerOptions = (availableProviders ?? []).map((p) => ({
    value: p.provider,
    label: PROVIDER_LABELS[p.provider] ?? p.provider,
  }));

  const modelOptions = (modelsData ?? []).map((m) => ({
    value: m.id,
    label: m.name ?? m.id,
  }));

  const isDisabled = !canManage;
  const isEnrichmentDisabled = isDisabled || saveEnrichmentMutation.isPending;

  return (
    <Card data-testid="doc-processing-enrichment-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-primary-600" />
          {t('Document Processing & Context Enrichment')}
        </CardTitle>
        <CardDescription>
          {t('Provider and model used for document OCR, summaries, and contextual enrichment. Changes apply immediately.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Provider dropdown — label-less, auto-saves (AC-11/12) */}
        <Select
          value={selectedProvider}
          onValueChange={handleProviderChange}
          options={providerOptions}
          placeholder={t('Select provider')}
          disabled={isDisabled || saveProviderMutation.isPending}
          data-testid="doc-processing-provider-select"
          aria-label={t('Document processing provider')}
        />

        {/* Model dropdown — filtered same as AIProviderForm (AC-11), auto-saves (AC-12) */}
        <Select
          value={selectedModel}
          onValueChange={handleModelChange}
          options={modelOptions}
          placeholder={isModelsLoading ? t('Loading…') : t('Select model')}
          disabled={isDisabled || isModelsLoading || saveModelMutation.isPending || !selectedProvider}
          data-testid="doc-processing-model-select"
          aria-label={t('Document processing model')}
        />

        {/* Contextual enrichment toggle — auto-saves (AC-12) */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <Switch
            checked={enrichmentEnabled}
            onChange={handleEnrichmentChange}
            disabled={isEnrichmentDisabled}
            data-testid="contextual-enrichment-toggle"
            aria-label={t('Contextual Enrichment')}
          />
          <div>
            <p className="text-sm font-medium">
              {t('Contextual Enrichment')}
              {saveEnrichmentMutation.isPending ? '…' : ''}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('Prepends a short context sentence to each chunk before embedding to improve retrieval.')}
            </p>
          </div>
        </div>

        {!canManage && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="doc-processing-no-permission-hint">
            {t('Requires the Manage Knowledge permission to change.')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
